import "./App.css";

import Box from "@mui/material/Box";
import React, { useEffect, useState } from "react";
import { AuthContextProps, useAuth as useOidcAuth } from "react-oidc-context";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { buildApiDirectory } from "./api/ApiDirectory";
import {
  buildPageConfigurations,
  buildPageDirectory,
} from "./api/PageDirectory";
import { getMyUser } from "./api/user/getMyUser";
import ApiVersionCheck from "./components/ApiVersionCheck";
import APModalStack from "./components/APModalStack";
import CrmLinkRouter from "./components/CrmLinkRouter";
import InitialSetupRoute from "./components/InitialSetupRoute";
import LoadingBackdrop from "./components/LoadingBackdrop";
import NavBar from "./components/NavBar";
import PageViewTracker from "./components/PageViewTracker";
import ProtectedRoute from "./components/ProtectedRoute";
import RestrictedAppScope from "./components/RestrictedAppScope";
import ScrollToTop from "./components/ScrollToTop";
import ShowMFAPrompt from "./components/ShowMFAPrompt";
import { Config } from "./config";
import ContactHelp from "./contact/contactHelp";
import ContactDossierListPage from "./contact/viewDossiers";
import ViewKeycloakUser from "./contact/viewKeycloakUser";
import CreateQuoteRedirect from "./createQuoteRedirect";
import ActionConfirmation from "./dossier/actionConfirmation";
import CreateExpenseClaims from "./dossier/activated/createExpenseClaim";
import DossierActivated from "./dossier/activated/home";
import ViewDossierBalances from "./dossier/activated/viewBalances";
import ViewDossierBudget from "./dossier/activated/viewBudget";
import ViewDossierBudgets from "./dossier/activated/viewBudgets";
import DossierDocumentList from "./dossier/activated/viewDocuments";
import DossierExpenseClaims from "./dossier/activated/viewExpenseClaims";
import DossierInvoiceComparison from "./dossier/activated/viewInvoiceComparison";
import DossierTransactionComparison from "./dossier/activated/viewTransactionComparison";
import DossierTransactions from "./dossier/activated/viewTransactions";
import CompareQuotes from "./dossier/compareQuotes";
import DirectDebit from "./dossier/directDebit";
import EmployerSetup from "./dossier/employerSetup";
import FinanceApplication from "./dossier/financeApplication";
import PackagingAgreement from "./dossier/packagingAgreement";
import QuoteComparison from "./dossier/quoteComparison";
import TerminateDossier from "./dossier/terminateDossier";
import DossierVehicleComparison from "./dossier/vehicleComparison";
import DossierVehicleDetail from "./dossier/vehicleDetail";
import DossierVehicleReview from "./dossier/vehicleReview";
import DossierVehicleSearch from "./dossier/vehicleSearch";
import DossierVehicleSearchResults from "./dossier/vehicleSearch/vehicleSearchResults";
import VehicleSourcingConfirmConfiguration from "./dossier/vehicleSourcing/vehicleSourcingConfirmConfiguration";
import VehicleSourcingConfirmVehicle from "./dossier/vehicleSourcing/vehicleSourcingConfirmVehicle";
import VehicleSourcingManufacturerOptions from "./dossier/vehicleSourcing/vehicleSourcingManufacturerOptions";
import VehicleSourcingMethod from "./dossier/vehicleSourcing/vehicleSourcingMethod";
import VehicleSourcingOptions from "./dossier/vehicleSourcing/vehicleSourcingOptions";
import VehicleSourcingSelectVehicle from "./dossier/vehicleSourcing/vehicleSourcingSelectVehicle";
import ViewDossier from "./dossier/viewDossier";
import RedirectOpportunityToDossier from "./dossier/viewDossier/RedirectOpportunityToDossier";
import RedirectToDossierFromStatus from "./dossier/viewDossier/RedirectToDossierFromStatus";
import RedirectToLatestDossierPage from "./dossier/viewDossier/RedirectToLatestDossierPage";
import DossierList from "./dossier/viewDossiers";
import ViewDossierTermination from "./dossier/viewDossierTermination";
import EmployerViewAnalyticsActiveLeases from "./employer/analytics/viewActiveLeases";
import EmployerViewAnalyticsAnnualSavings from "./employer/analytics/viewAnnualSavings";
import EmployerViewAnalyticsPayrollTaxSavings from "./employer/analytics/viewPayrollTaxSavings";
import EmployerViewAnalyticsPayrollTaxSavingsSummaries from "./employer/analytics/viewPayrollTaxSavingsSummaries";
import EmployerViewAnalyticsSavingsSummaries from "./employer/analytics/viewSavingsSummaries";
import EmployerViewAnalyticsVehicleSettlementSurveyResponses from "./employer/analytics/viewVehicleSettlementSurveyResponses";
import ApproveDrivers from "./employer/approveDrivers";
import EmployerHome from "./employer/home";
import EmployerTerminateDossier from "./employer/terminateDossier";
import EmployerDocumentList from "./employer/viewDocuments";
import EmployerDossierList from "./employer/viewDossiers";
import EmployerViewDossierTermination from "./employer/viewDossierTermination";
import EmployerViewSurveyFeedback from "./employer/viewSurveyFeedback";
import ViewFaq from "./faq/viewFaq";
import ViewAppLocationFaqs from "./faq/viewFaqList";
import { stripOidcQueryParamsFromSearchParams } from "./helpers/routes";
import Home from "./home";
import { ApiProvider } from "./providers/ApiProvider";
import { APQueryProvider } from "./providers/APQueryProvider";
import { AuthProvider, useAuth } from "./providers/AuthProvider";
import { ConfirmProvider } from "./providers/ConfirmProvider";
import CreateQuote, { QuoteCreateAction } from "./quote/createQuote";
import QuoteFinanceComparison from "./quote/financeComparison";
import PersonaliseQuote from "./quote/personaliseQuote";
import RequestDealerPricing from "./quote/requestVehiclePricing";
import SearchDealerPricingRequests from "./quote/searchVehiclePricingRequests";
import SelectDealerPricing from "./quote/selectDealerPricing";
import ViewQuote from "./quote/viewQuote";
import ViewQuoteCalculations from "./quote/viewQuoteCalculations";
import ViewQuoteVehicleSuggestions from "./quote/viewQuoteVehicleSuggestions";
import ViewSurvey from "./survey/viewSurvey";
import UserInfo from "./userInfo";
import EnableMFA from "./userInfo/enableMFA";

function App() {
  const auth = useOidcAuth();
  const { inAppScope, isAuthorised, getScopedPageUrl } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [modalStack, setModalStack] = useState([] as string[]);
  const modalPath = searchParams.get("modal");
  const modalType = searchParams.get("modal.type");
  const isPersistentModal = modalType === "persistent";
  const employerId = searchParams.get("EmployerID") ?? undefined;

  if (modalPath) {
    // If navigating to previous modal,then pop modal stack
    if (location.state?.popModalStack) {
      location.state.popModalStack = null;
      const newStack = modalStack.slice(0, modalStack.length - 1);

      setModalStack(newStack);
    } else {
      // Else we add to stack if it is a new modal path
      const lastModalPath =
        (modalStack.length > 0 && modalStack[modalStack.length - 1]) || null;

      if (!lastModalPath || lastModalPath !== modalPath) {
        setModalStack([...modalStack, modalPath]);
      }
    }
  } else if (modalStack.length > 0) {
    setModalStack([]);
  }

  const {
    data: me,
    isLoading,
    isSuccess,
    isError,
    error,
  } = getMyUser(employerId);

  if (isError) {
    throw error;
  }

  return (
    <>
      {isSuccess && auth.isAuthenticated && (
        <React.Fragment>
          <RestrictedAppScope scope="external">
            <NavBar me={me} />
            <ShowMFAPrompt mfaEnabled={me.shouldPromptMFA} />
          </RestrictedAppScope>
          <RestrictedAppScope scope="internal">
            <Box m={2} />
          </RestrictedAppScope>
          <PageViewTracker me={me} />
          <ScrollToTop />
          <ApiVersionCheck />
          {inAppScope("internal") && isAuthorised("internal", me) && (
            <Routes>
              <Route
                path={getScopedPageUrl("home")}
                element={<div>Internal Home</div>}
              />
              <Route
                path={getScopedPageUrl("crmLinkRouter", ":destinationId")}
                element={<CrmLinkRouter />}
              />
              <Route
                path={getScopedPageUrl("viewDossierFromStatus", ":dossierId")}
                element={<RedirectToDossierFromStatus />}
              />
              <Route
                path={getScopedPageUrl("viewDossier", ":dossierId")}
                element={<ViewDossier />}
              />
              <Route
                path={getScopedPageUrl("dossierActivated", ":dossierId")}
                element={<DossierActivated />}
              />
              <Route
                path={getScopedPageUrl("dossierExpenseClaims", ":dossierId")}
                element={<DossierExpenseClaims />}
              />
              <Route
                path={getScopedPageUrl("dossierTransactions", ":dossierId")}
                element={<DossierTransactions />}
              />
              <Route
                path={getScopedPageUrl(
                  "dossierTransactionComparison",
                  ":dossierId"
                )}
                element={<DossierTransactionComparison />}
              />
              <Route
                path={getScopedPageUrl(
                  "dossierInvoiceComparison",
                  ":dossierId"
                )}
                element={<DossierInvoiceComparison />}
              />
              <Route
                path={getScopedPageUrl("dossierBudgets", ":dossierId")}
                element={<ViewDossierBudgets />}
              />
              <Route
                path={getScopedPageUrl(
                  "dossierBudget",
                  ":dossierId",
                  ":budgetId"
                )}
                element={<ViewDossierBudget />}
              />
              <Route
                path={getScopedPageUrl("dossierBalances", ":dossierId")}
                element={<ViewDossierBalances />}
              />
              <Route
                path={getScopedPageUrl("dossierDocuments", ":dossierId")}
                element={<DossierDocumentList />}
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleDetail",
                  ":dossierId",
                  ":vehicleCode"
                )}
                element={<DossierVehicleDetail />}
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleReview",
                  ":dossierId",
                  ":vehicleCode"
                )}
                element={<DossierVehicleReview />}
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleComparison",
                  ":dossierId"
                )}
                element={<DossierVehicleComparison />}
              />
              <Route
                path={getScopedPageUrl("dossierVehicleSearch", ":dossierId")}
                element={<DossierVehicleSearch />}
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleSearchResults",
                  ":dossierId"
                )}
                element={<DossierVehicleSearchResults />}
              />
              <Route
                path={getScopedPageUrl("compareQuotes", ":dossierId")}
                element={<CompareQuotes />}
              />
              <Route
                path={getScopedPageUrl("viewOpportunity", ":opportunityId")}
                element={<RedirectOpportunityToDossier />}
              />
              <Route
                path={getScopedPageUrl("createQuote", ":dossierId")}
                element={
                  <CreateQuote me={me} action={QuoteCreateAction.Create} />
                }
              />
              <Route
                path={getScopedPageUrl("viewQuote", ":quoteId")}
                element={<ViewQuote />}
              />
              <Route
                path={getScopedPageUrl(
                  "viewQuoteVehicleSuggestions",
                  ":quoteId"
                )}
                element={<ViewQuoteVehicleSuggestions />}
              />
              <Route
                path={getScopedPageUrl("viewQuoteCalculations", ":quoteId")}
                element={<ViewQuoteCalculations />}
              />
              <Route
                path={getScopedPageUrl("viewQuoteComparisons", ":dossierId")}
                element={<QuoteComparison />}
              />
              <Route
                path={getScopedPageUrl(
                  "viewQuoteFinanceComparison",
                  ":quoteId"
                )}
                element={<QuoteFinanceComparison />}
              />
              <Route
                path={getScopedPageUrl("personaliseQuote", ":quoteId")}
                element={<PersonaliseQuote />}
              />
              <Route
                path={getScopedPageUrl("requestDealerPricing", ":quoteId")}
                element={<RequestDealerPricing />}
              />
              <Route
                path={getScopedPageUrl("selectDealerPricing", ":quoteId")}
                element={<SelectDealerPricing />}
              />
              <Route
                path={getScopedPageUrl("searchDealerPricingRequests")}
                element={<SearchDealerPricingRequests />}
              />
              <Route
                path={getScopedPageUrl("editQuote", ":quoteId")}
                element={
                  <CreateQuote me={me} action={QuoteCreateAction.Edit} />
                }
              />
              <Route
                path={getScopedPageUrl("cloneQuote", ":quoteId")}
                element={
                  <CreateQuote me={me} action={QuoteCreateAction.Clone} />
                }
              />
              <Route
                path={getScopedPageUrl("viewKeycloakUser", ":contactId")}
                element={<ViewKeycloakUser />}
              />
              <Route
                path={getScopedPageUrl("viewContactDossiers", ":contactId")}
                element={<ContactDossierListPage />}
              />
              <Route
                path="*"
                element={<Navigate to={getScopedPageUrl("home")} />}
              />
            </Routes>
          )}
          {inAppScope("external") && isAuthorised("external", me) && (
            <Routes>
              <Route
                path={getScopedPageUrl("viewDossiers")}
                element={
                  <ProtectedRoute me={me}>
                    <DossierList />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("viewDossierFromStatus", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <RedirectToDossierFromStatus />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("latestDossierRedirect", ":destination")}
                element={
                  <ProtectedRoute me={me}>
                    <RedirectToLatestDossierPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("viewDossier", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <ViewDossier />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("viewQuoteComparisons", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <QuoteComparison />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "viewQuoteFinanceComparison",
                  ":quoteId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <QuoteFinanceComparison />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("createQuote", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <CreateQuote me={me} action={QuoteCreateAction.Create} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("dossierEmployerSetup", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "dossierFinanceApplication",
                  ":dossierId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <FinanceApplication />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "dossierPackagingAgreement",
                  ":dossierId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <PackagingAgreement />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleSourcingMethod",
                  ":dossierId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <VehicleSourcingMethod />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleSourcingSelectVehicle",
                  ":dossierId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <VehicleSourcingSelectVehicle />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleSourcingConfirmVehicle",
                  ":dossierId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <VehicleSourcingConfirmVehicle />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleSourcingOptions",
                  ":dossierId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <VehicleSourcingOptions />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleSourcingManufacturerOptions",
                  ":dossierId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <VehicleSourcingManufacturerOptions />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleSourcingConfirmConfiguration",
                  ":dossierId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <VehicleSourcingConfirmConfiguration />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("dossierDirectDebit", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <DirectDebit />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "dossierActionConfirmation",
                  ":dossierId",
                  ":eventId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <ActionConfirmation />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("dossierActivated", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <DossierActivated />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("dossierTerminate", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <TerminateDossier />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("dossierViewTermination", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <ViewDossierTermination />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("dossierExpenseClaims", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <DossierExpenseClaims />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("dossierTransactions", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <DossierTransactions />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("dossierBudgets", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <ViewDossierBudgets />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "dossierBudget",
                  ":dossierId",
                  ":budgetId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <ViewDossierBudget />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("dossierBalances", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <ViewDossierBalances />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("dossierDocuments", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <DossierDocumentList />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleDetail",
                  ":dossierId",
                  ":vehicleCode"
                )}
                element={<DossierVehicleDetail />}
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleReview",
                  ":dossierId",
                  ":vehicleCode"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <DossierVehicleReview />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("dossierVehicleSearch", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <DossierVehicleSearch />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleSearchResults",
                  ":dossierId"
                )}
                element={<DossierVehicleSearchResults />}
              />
              <Route
                path={getScopedPageUrl(
                  "dossierVehicleComparison",
                  ":dossierId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <DossierVehicleComparison />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("compareQuotes", ":dossierId")}
                element={
                  <ProtectedRoute me={me}>
                    <CompareQuotes />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "createDossierExpenseClaim",
                  ":dossierId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <CreateExpenseClaims />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerHome")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerHome me={me} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerApproveDrivers")}
                element={
                  <ProtectedRoute me={me}>
                    <ApproveDrivers me={me} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerInvoices")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerDocumentList me={me} page={"employerInvoices"} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerPackagingAgreements")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerDocumentList
                      me={me}
                      page={"employerPackagingAgreements"}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerPayrollAdvice")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerDocumentList
                      me={me}
                      page={"employerPayrollAdvice"}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerFBTReports")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerDocumentList me={me} page={"employerFBTReports"} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerEngagementReports")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerDocumentList
                      me={me}
                      page={"employerEngagementReports"}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerPromotions")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerDocumentList me={me} page={"employerPromotions"} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerLuxuryLeaseReports")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerDocumentList
                      me={me}
                      page={"employerLuxuryLeaseReports"}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerViewDossiers")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerDossierList me={me} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "employerTerminateDossier",
                  ":dossierId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerTerminateDossier me={me} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "employerViewDossierTermination",
                  ":dossierId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerViewDossierTermination me={me} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerViewSurveyFeedback")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerViewSurveyFeedback me={me} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerViewAnalyticsActiveLeases")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerViewAnalyticsActiveLeases me={me} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerViewAnalyticsAnnualSavings")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerViewAnalyticsAnnualSavings me={me} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "employerViewAnalyticsPayrollTaxSavings"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerViewAnalyticsPayrollTaxSavings me={me} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "employerViewAnalyticsVehicleSettlementSurveyResponses"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerViewAnalyticsVehicleSettlementSurveyResponses
                      me={me}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("employerViewAnalyticsSavingsSummaries")}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerViewAnalyticsSavingsSummaries me={me} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "employerViewAnalyticsPayrollTaxSavingsSummaries"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <EmployerViewAnalyticsPayrollTaxSavingsSummaries me={me} />
                  </ProtectedRoute>
                }
              />

              <Route
                path={getScopedPageUrl("viewQuote", ":quoteId")}
                element={
                  <ProtectedRoute me={me}>
                    <ViewQuote />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("createQuoteRedirect")}
                element={
                  <ProtectedRoute me={me}>
                    <CreateQuoteRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl(
                  "viewQuoteVehicleSuggestions",
                  ":quoteId"
                )}
                element={
                  <ProtectedRoute me={me}>
                    <ViewQuoteVehicleSuggestions />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("personaliseQuote", ":quoteId")}
                element={
                  <ProtectedRoute me={me}>
                    <PersonaliseQuote />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("editQuote", ":quoteId")}
                element={
                  <ProtectedRoute me={me}>
                    <CreateQuote me={me} action={QuoteCreateAction.Edit} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("cloneQuote", ":quoteId")}
                element={
                  <ProtectedRoute me={me}>
                    <CreateQuote me={me} action={QuoteCreateAction.Clone} />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("viewSurvey", ":surveyId")}
                element={
                  <ProtectedRoute me={me}>
                    <ViewSurvey />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("initialUser")}
                element={
                  <InitialSetupRoute me={me}>
                    <UserInfo />
                  </InitialSetupRoute>
                }
              />
              <Route
                path={getScopedPageUrl("userDirectDebit")}
                element={
                  <ProtectedRoute me={me}>
                    <DirectDebit />
                  </ProtectedRoute>
                }
              />
              <Route
                path={getScopedPageUrl("home")}
                element={
                  <ProtectedRoute me={me}>
                    <Home me={me} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="*"
                element={
                  <Navigate
                    to={`${getScopedPageUrl("home")}${location.search}`}
                  />
                }
              />
            </Routes>
          )}
          {modalStack.length > 0 && (
            <Routes
              location={{
                ...location,
                pathname: modalStack[modalStack.length - 1],
              }}
            >
              <Route
                element={
                  <APModalStack
                    modalStack={modalStack}
                    appLocation={location}
                    size="medium"
                    hideClose={isPersistentModal}
                    disableBackdropClose={isPersistentModal}
                    backOnClose={isPersistentModal}
                  />
                }
              >
                <Route
                  path={getScopedPageUrl("modalFaq", ":faqId")}
                  element={<ViewFaq />}
                />
                <Route
                  path={getScopedPageUrl("modalAppLocation", ":appLocationId")}
                  element={<ViewAppLocationFaqs />}
                />

                <Route
                  path={getScopedPageUrl(
                    "modalAppLocationFaq",
                    ":appLocationId",
                    ":faqId"
                  )}
                  element={<ViewAppLocationFaqs />}
                />

                {inAppScope("external") && (
                  <Route
                    path={getScopedPageUrl("modalContactHelp")}
                    element={<ContactHelp appLocation={location} />}
                  />
                )}
              </Route>
              <Route
                element={
                  <APModalStack
                    modalStack={modalStack}
                    appLocation={location}
                    size="small"
                    hideClose
                    disableBackdropClose
                  />
                }
              >
                {inAppScope("external") && (
                  <Route
                    path={getScopedPageUrl("modalEnableMFA")}
                    element={<EnableMFA me={me} appLocation={location} />}
                  />
                )}
              </Route>
            </Routes>
          )}
        </React.Fragment>
      )}
      {isLoading && <LoadingBackdrop />}
    </>
  );
}

function ApiWrappedApp() {
  const auth = useOidcAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 1. Handle Redirect to Login
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator) {
      auth.signinRedirect();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator]);

  // 2. Proactive Session Check (Handles Admin Kicks & Refresh)
  useEffect(() => {
    if (auth.isAuthenticated && !auth.activeNavigator) {
      // Direct call updates auth.error if it fails
      auth.signinSilent().catch(() => {
        /* Handled by error listener below */
      });
    }
  }, [auth.isAuthenticated]);

  // 3. Handle logout from other tabs
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      // react-oidc-context / oidc-client-ts stores user info in localStorage
      if (event.key?.includes("oidc.user:") && event.newValue === null) {
        auth.removeUser();
      }
    };

    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, [auth]);

  // 4. Centralized Auth Error & Event Management
  useEffect(() => {
    const cleanup = () => {
      console.warn("Session lost or terminated. Cleaning up...");
      auth.removeUser();
    };

    // Listen for the iframe/server logout event (Production/HTTPS)
    const unbindLogout = auth.events.addUserSignedOut(cleanup);

    // Watch for "Session not active" errors (Localhost/Manual Ping)
    if (auth.error) {
      const msg = auth.error.message.toLowerCase();
      if (
        msg.includes("session not active") ||
        msg.includes("invalid_grant") ||
        msg.includes("login_required") ||
        msg.includes("access_denied")
      ) {
        cleanup();
      }
    }

    return () => unbindLogout();
  }, [auth.error, auth.events, auth.removeUser]);

  // 5. URL Cleanup: Remove OIDC params after successful login
  useEffect(() => {
    if (auth.isAuthenticated) {
      const urlParams = new URLSearchParams(location.search);
      const before = urlParams.toString();
      stripOidcQueryParamsFromSearchParams(urlParams);

      if (urlParams.toString() !== before) {
        const newSearch = urlParams.toString();
        const suffix = newSearch ? `?${newSearch}` : "";
        navigate(`${location.pathname}${suffix}`, { replace: true });
      }
    }
  }, [auth.isAuthenticated, location.pathname, location.search, navigate]);

  return (
    <>
      {auth.isAuthenticated && auth.user && (
        <ApiProvider
          auth={auth as AuthContextProps}
          baseUrl={Config.apiUrl}
          apiDirectory={buildApiDirectory(Config.applicationScope)}
        >
          <AuthProvider
            pageDirectory={buildPageDirectory(Config.applicationScope)}
            pageConfigurations={buildPageConfigurations(
              Config.applicationScope
            )}
          >
            <APQueryProvider>
              <ConfirmProvider>
                <App />
              </ConfirmProvider>
            </APQueryProvider>
          </AuthProvider>
        </ApiProvider>
      )}
    </>
  );
}

export default ApiWrappedApp;
